/**
 * ModelUnitsDrawer — the units-on-a-model panel with a per-unit MODEL MOVE.
 *
 * Lists exactly which units are on a model (useful for a retired one). Moving a
 * unit to another model is the middle step of the safe "code rename"
 * (create → move → retire): it regenerates the unit's code in place (same UUID),
 * keeping its suffix, and requires reprinting its QR sticker. The server guards
 * against active holds/rentals and retired targets; here we surface the outcome.
 */
import { useCallback, useEffect, useState } from 'react';
import { Loader2, ArrowRightLeft } from 'lucide-react';
import { AdminFormDrawer } from '@/components/settings/AdminFormDrawer';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { getAdminUnits, moveUnitModel, bulkMoveUnitsToModel, type AdminUnitRow } from '@/lib/adminApi';
import { toast } from 'react-toastify';
import type { FilterModel } from '@/types';

interface Props {
  /** The model whose units to show, or null when closed. */
  model: FilterModel | null;
  /** All models — the move-target picker offers the ACTIVE ones except this. */
  allModels: FilterModel[];
  onClose: () => void;
  /** Called after a successful move so the parent can refresh model counts. */
  onChanged: () => void;
}

export function ModelUnitsDrawer({ model, allModels, onClose, onChanged }: Props) {
  const [units, setUnits] = useState<AdminUnitRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [movingId, setMovingId] = useState<string | null>(null);
  const [target, setTarget] = useState('');
  const [busy, setBusy] = useState(false);
  const [bulkTarget, setBulkTarget] = useState('');

  const load = useCallback(async (code: string) => {
    setLoading(true);
    setMovingId(null);
    setTarget('');
    setBulkTarget('');
    try {
      const res = await getAdminUnits({ models: [code] });
      setUnits(res.units || []);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to load units');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (model?.code) void load(model.code);
  }, [model, load]);

  const targets = allModels.filter((m) => m.code && m.code !== model?.code && m.is_active !== false);

  const doMove = async (unitId: string) => {
    if (!target || !model?.code) return;
    setBusy(true);
    try {
      const res = await moveUnitModel(unitId, target);
      toast.success(`Moved to ${res.new_unit_code} — reprint this unit's QR label.`);
      onChanged();
      await load(model.code);
    } catch (err) {
      // Server messages are descriptive (on rental/hold, retired target, etc.).
      toast.error(err instanceof Error ? err.message : 'Move failed');
    } finally {
      setBusy(false);
    }
  };

  const doBulkMove = async () => {
    if (!bulkTarget || !model?.code) return;
    if (!confirm(`Move all ${units.length} unit(s) from ${model.code} to ${bulkTarget}? Units on an active rental or hold are skipped. Moved units get new codes (reprint their QR labels).`)) return;
    setBusy(true);
    try {
      const res = await bulkMoveUnitsToModel(model.code, bulkTarget);
      const parts = [`Moved ${res.moved.length}`];
      if (res.skipped.length) parts.push(`skipped ${res.skipped.length} (on rental/hold)`);
      if (res.failed.length) parts.push(`${res.failed.length} failed`);
      const msg = `${parts.join(', ')} → ${res.to_model}. Reprint moved units' QR labels.`;
      if (res.failed.length) toast.error(msg);
      else if (res.skipped.length) toast.warn(msg);
      else toast.success(msg);
      onChanged();
      await load(model.code);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Bulk move failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AdminFormDrawer
      open={model !== null}
      onClose={onClose}
      title={model ? `Units on ${model.code}` : ''}
    >
      {model && (
        <div className="space-y-4">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading units…
            </div>
          ) : units.length === 0 ? (
            <p className="text-sm text-muted-foreground">No units are assigned to this model.</p>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">
                {units.length} {units.length === 1 ? 'unit is' : 'units are'} on model{' '}
                <span className="font-mono text-foreground">{model.code}</span>. Hover a unit and use
                the move icon to re-home it, or move them all at once below.
              </p>

              {/* Bulk move-all bar */}
              <div className="rounded-md border border-border bg-muted/20 p-3">
                <label className="mb-1.5 block text-sm font-medium text-foreground">
                  Move all {units.length} unit{units.length === 1 ? '' : 's'} to
                </label>
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <Select value={bulkTarget} onValueChange={setBulkTarget}>
                      <SelectTrigger className="bg-background border-border">
                        <SelectValue placeholder="Choose a model…" />
                      </SelectTrigger>
                      <SelectContent>
                        {targets.map((t) => (
                          <SelectItem key={t.id} value={t.code!}>{t.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    className="whitespace-nowrap bg-[#8FB560] hover:bg-[#7a9d51]"
                    disabled={busy || !bulkTarget}
                    onClick={() => void doBulkMove()}
                  >
                    <ArrowRightLeft className="mr-2 h-4 w-4" /> Move all
                  </Button>
                </div>
                <p className="mt-1.5 text-xs text-muted-foreground">
                  Units on an active rental or hold are skipped and reported.
                </p>
              </div>
              <div className="max-h-[55vh] space-y-1 overflow-y-auto">
                {units.map((u) => (
                  <div key={u.id} className="rounded-md border border-border/50">
                    <div className="group flex items-center gap-3 rounded-md px-3 py-2.5 transition-colors hover:bg-muted/40">
                      <span className="font-mono text-sm text-foreground">{u.unit_code}</span>
                      <span className="flex-1 truncate text-sm text-muted-foreground">
                        {u.companies?.name || u.company?.name || '—'}
                      </span>
                      <button
                        type="button"
                        title="Move to another model"
                        aria-label={`Move ${u.unit_code} to another model`}
                        onClick={() => { setMovingId(movingId === u.id ? null : u.id); setTarget(''); }}
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground opacity-60 transition-all hover:bg-muted hover:text-[#8FB560] group-hover:opacity-100"
                      >
                        <ArrowRightLeft className="h-4 w-4" />
                      </button>
                    </div>
                    {movingId === u.id && (
                      <div className="flex items-end gap-2 border-t border-border/50 px-3 py-3">
                        <div className="flex-1">
                          <label className="mb-1.5 block text-sm text-muted-foreground">Move to model</label>
                          <Select value={target} onValueChange={setTarget}>
                            <SelectTrigger className="bg-background border-border">
                              <SelectValue placeholder="Choose a model…" />
                            </SelectTrigger>
                            <SelectContent>
                              {targets.map((t) => (
                                <SelectItem key={t.id} value={t.code!}>{t.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <Button
                          className="whitespace-nowrap bg-[#8FB560] hover:bg-[#7a9d51]"
                          disabled={busy || !target}
                          onClick={() => void doMove(u.id)}
                        >
                          <ArrowRightLeft className="mr-2 h-4 w-4" /> Move
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <p className="border-t border-border pt-3 text-sm text-muted-foreground">
                A unit's code embeds its model (e.g. <span className="font-mono">1RE191TCA</span>),
                so a move regenerates the code (same unit, new number) — its QR token still resolves,
                but the printed sticker must be reprinted. Blocked while a unit is on an active rental
                or hold.
              </p>
            </>
          )}
        </div>
      )}
    </AdminFormDrawer>
  );
}
